import React, { FC, Fragment, useId } from "react";
import { Link, NavLink } from "react-router-dom";
import styles from "./Navbar.module.css";
import UserMenu from "./UserMenu";
import Cart from "./Cart";
import SearchBar from "../UILibrary/SearchBar";
import CompanyLogo from "Utils/Assets/SVGs/CompanyLogo";
import MenuIcon from "Utils/Assets/SVGs/MenuIcon";
import type { INavbarProps } from "../../../Model/NavBar.model";
import { useNavbar } from "../../../Logic/Functions/Hooks/useNavbar";

const Navbar: FC<INavbarProps> = ({ brand = "ChimeraTech", cartCount = 0 }) => {
  const {
    q,
    setQ,
    open,
    setOpen,
    closeMenu,
    menuPanelRef,
    submitSearch,
    badgeCount,

    headerRef,
    navHeight,
    isSticky,
  } = useNavbar({ cartCountProp: cartCount });

  const menuId = `menu-${useId()}`;

  return (
    <Fragment>
      {isSticky && (
        <div
          data-testid="navbar-spacer"
          aria-hidden
          style={{ height: navHeight }}
        />
      )}

      <header
        ref={headerRef}
        className={`${styles.wrapperNav} ${isSticky ? styles.wrapperNavSticky : ""
          }`}
        data-testid="navbar"
      >
        <button
          type="button"
          className={styles.backdrop}
          aria-hidden={!open}
          hidden={!open}
          onClick={closeMenu}
          data-testid="navbar-backdrop"
        />

        <nav
          className={styles.bar}
          data-open={open}
          aria-label="Main"
          data-testid="navbar-bar"
        >
          <div className={styles.brandRow} data-testid="navbar-brand-row">
            <Link
              to="/"
              className={styles.logo}
              onClick={closeMenu}
              data-testid="navbar-logo-link"
            >
              <CompanyLogo
                width={28}
                height={28}
                aria-hidden
                className={styles.brandMark}
                data-testid="navbar-logo-icon"
              />
              <span className={styles.brandText} data-testid="navbar-brand-text">
                {brand}
              </span>
            </Link>

            <div className={styles.brandActions}>
              <div
                className={styles.mobileOnly}
                data-testid="navbar-user-menu-mobile"
              >
                <UserMenu />
              </div>

              <button
                className={styles.menuToggle}
                aria-controls={menuId}
                aria-expanded={open}
                aria-label="Toggle navigation"
                onClick={() => setOpen((prev) => !prev)}
                data-testid="navbar-menu-toggle"
              >
                <MenuIcon
                  className={styles.menuIcon}
                  width={24}
                  height={24}
                  data-testid="navbar-menu-icon"
                />
              </button>
            </div>
          </div>

          <div
            id={menuId}
            ref={menuPanelRef}
            className={styles.menuPanel}
            data-testid="navbar-menu-panel"
          >
            <ul className={styles.links} data-testid="navbar-links">
              <li data-testid="navbar-item-shop">
                <NavLink
                  to="/products"
                  className={styles.link}
                  onClick={closeMenu}
                  data-testid="navbar-link-shop"
                >
                  Shop
                </NavLink>
              </li>

              <li data-testid="navbar-item-about">
                <NavLink
                  to="/about"
                  className={styles.link}
                  onClick={closeMenu}
                  data-testid="navbar-link-about"
                >
                  About us
                </NavLink>
              </li>

              <li data-testid="navbar-item-contact">
                <NavLink
                  to="/contact"
                  className={styles.link}
                  onClick={closeMenu}
                  data-testid="navbar-link-contact"
                >
                  Contact us
                </NavLink>
              </li>
            </ul>

            <form
              className={styles.searchForm}
              onSubmit={submitSearch}
              role="search"
              aria-label="Site"
              data-testid="navbar-search-form"
            >
              <div data-testid="navbar-searchbar">
                <SearchBar
                  value={q}
                  onChange={setQ}
                  placeholder="Search products..."
                />
              </div>
              <button
                type="submit"
                className={styles.hiddenSubmit}
                aria-hidden
                data-testid="navbar-search-submit"
              />
            </form>

            <div
              className={styles.rightCluster}
              data-testid="navbar-right-cluster"
            >
              <div
                className={styles.desktopOnly}
                data-testid="navbar-user-menu-desktop"
              >
                <UserMenu />
              </div>

              <div className={styles.cartCluster} data-testid="navbar-cart">
                <div className={styles.cartIconWrap}>
                  <Cart count={badgeCount} />
                </div>
              </div>
            </div>
          </div>
        </nav>
      </header>
    </Fragment>
  );
};

export default Navbar;
